import logoHorizontal from "@/assets/brand/scheda-logo-horizontal.svg";
import logoHorizontalReversed from "@/assets/brand/scheda-logo-horizontal-reversed.svg";

/**
 * The Scheda horizontal logo — the calendar mark plus the wordmark, from the
 * same SVG the landing page and the sign-in modal already use. One asset, so
 * the brand cannot drift between the marketing surface and the app.
 *
 * <p>The artwork also matches {@code public/app-icon-512.png} and
 * {@code public/favicon.svg} (which is byte-identical to
 * {@code assets/brand/scheda-mark-color.svg}). That equivalence is the point:
 * Google's OAuth verification checks that the logo on the consent screen is the
 * logo a user actually sees on the homepage and the privacy policy.</p>
 *
 * <h2>Why two images rather than one</h2>
 * The wordmark is baked into the SVG as filled paths in ink ({@code #171A1D}),
 * so it vanishes on a dark surface. {@code scheda-logo-horizontal-reversed.svg}
 * is the same lockup with a white wordmark. Which one applies depends on the
 * SURFACE, not on the theme alone:
 *
 * <ul>
 *   <li>{@code surface="auto"} — chrome that follows the theme, e.g. AppNav sits
 *       on {@code bg-background}. Both images render and CSS picks one, so there
 *       is no theme hook to call and nothing to resolve during SSR.</li>
 *   <li>{@code surface="light"} — surfaces that stay light in EVERY theme. The
 *       {@code .landing}-scoped legal pages are exactly that: they hard-code
 *       {@code text-slate-900} on white, so {@code auto} would put the white
 *       wordmark on a white background whenever the user's theme is dark.</li>
 * </ul>
 *
 * <p>Deliberately hook-free. This renders inside the apex catalog bundle, which
 * has no providers at all — see the invariant in {@code catalog/CatalogApp.tsx}.</p>
 *
 * @param alt image alt text. Pass {@code ""} when the caller already labels the
 *     wrapping link, so a screen reader does not announce "Scheda" twice.
 */
export function SchedaLockup({
  className = "h-7 w-auto",
  surface = "auto",
  alt = "Scheda",
}: {
  className?: string;
  surface?: "auto" | "light";
  alt?: string;
}) {
  if (surface === "light") {
    return <img src={logoHorizontal} alt={alt} className={className} />;
  }
  return (
    <>
      <img src={logoHorizontal} alt={alt} className={`${className} dark:hidden`} />
      <img
        src={logoHorizontalReversed}
        alt=""
        aria-hidden="true"
        className={`${className} hidden dark:block`}
      />
    </>
  );
}
